import { useEffect, useRef } from 'react';

const features = [
  {
    icon: 'ri-shield-keyhole-line',
    title: 'Military-Grade Encryption',
    description: 'AES-256 encryption keeps all your data safe from hackers, ISPs, and surveillance on any network.',
  },
  {
    icon: 'ri-eye-off-line',
    title: 'Zero-Log Privacy',
    description: 'We never track, store, or sell your browsing history or personal data. Your activity stays yours.',
  },
  {
    icon: 'ri-cpu-line',
    title: 'Performance Optimized',
    description: 'Intelligent caching and background cleanup keep your device responsive without draining the battery.',
  },
  {
    icon: 'ri-wifi-off-line',
    title: 'Kill Switch Protection',
    description: 'Automatically cuts internet access if the VPN drops, so your real IP is never accidentally exposed.',
  },
  {
    icon: 'ri-delete-bin-5-line',
    title: 'Deep Junk Cleaner',
    description: 'Detect and remove hidden caches, temp files, and residual data that waste precious storage space.',
  },
  {
    icon: 'ri-image-edit-line',
    title: 'Duplicate Photo Scanner',
    description: 'Identify and delete identical or near-duplicate images to reclaim gigabytes of space in seconds.',
  },
  {
    icon: 'ri-battery-2-charge-line',
    title: 'Battery Health Monitor',
    description: 'Track battery cycles, detect power-hungry apps, and get tailored recommendations to extend battery life.',
  },
  {
    icon: 'ri-global-line',
    title: '90+ Global Servers',
    description: 'Access high-speed VPN servers across 90+ locations for a seamless, low-latency connection everywhere.',
  },
];

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('[data-animate]').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-32 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center mb-20">
          <div
            data-animate
            className="inline-block text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-5 transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif" }}
          >
            Features
          </div>
          <h2
            data-animate
            className="text-white font-bold mb-5 leading-tight transition-all duration-700"
            style={{
              opacity: 0,
              transform: 'translateY(20px)',
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              transitionDelay: '120ms',
            }}
          >
            Everything You Need. Nothing You Don't.
          </h2>
          <p
            data-animate
            className="text-zinc-400 max-w-xl mx-auto leading-relaxed transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif", transitionDelay: '240ms' }}
          >
            Thoughtfully engineered features that solve real problems for iOS users every single day.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => (
            <div
              key={feature.title}
              data-animate
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 group hover:border-emerald-500/40 transition-all duration-500"
              style={{ opacity: 0, transform: 'translateY(24px)', transitionDelay: `${300 + i * 70}ms` }}
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-5 group-hover:bg-emerald-500/20 transition-colors duration-300">
                <i className={`${feature.icon} text-emerald-400 text-xl`}></i>
              </div>
              <h4 className="text-white font-semibold text-sm mb-3 leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <a>{feature.title}</a>
              </h4>
              <p className="text-zinc-500 text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
